// @flow
import type {Callback} from '../types/callback';
import type {Cancelable} from '../types/cancelable';
import type {LngLatLike} from '../geo/lng_lat';
import config from '../util/config';

import {getJSON} from "../util/ajax";
import urls from '../util/urls';
import {Common} from '../util/enums';
import LngLat from '../geo/lng_lat';
import {getApiTokenOrKey} from '../util/util';

type Address = {
    addr?: string,
    city?: string,
    state?: string,
    zip?: string,
    country?: string,
    region?: string,
    dataset?: string,
    searchString: string,
    postcodeFilter: string,
    splc: string
}

type GeocodeOptions = {
    address: Address,
    listSize?: number,
    success: Callback<mixed>,
    failure?: Callback<mixed>
}

type ReverseGeocodeOptions = {
    lonLat: LngLatLike,
    region?: string,
    dataset?: string,
    matchNamedRoadsOnly?: boolean,
    maxCleanupMiles?: number,
    success: Callback<mixed>,
    failure?: Callback<mixed>
}

/**
 * Geocoder
 */
export const Geocoder = {
    /**
     * Geocode
     *
     * @param {GeocodeOptions} Options
     *
     * @example
     * var geocodeLocation = TrimbleMaps.Geocoder.geocode({
     *   address: {
     *       addr: '1 Independence Way',
     *       city: 'Princeton',
     *       state: 'NJ',
     *       zip: '08540',
     *       region: TrimbleMaps.Common.Region.NA
     *   },
     *   listSize: 1,
     *   success: function(response) {
     *       console.log(response);
     *   },
     *   failure: function(response) {
     *       console.log(response);
     *   }
     * });
     */
    geocode,
    /**
     * Reverse Geocode
     *
     * @param {ReverseGeocodeOptions} Options
     *
     * @example
     * var reverseGeocodeLocation = TrimbleMaps.Geocoder.reverseGeocode({
     *   lonLat: new TrimbleMaps.LngLat(-122.31693, 47.60784),
     *   region: TrimbleMaps.Common.Region.NA,
     *   success: function(response) {
     *       console.log(response);
     *   },
     *   failure: function(response) {
     *       console.log(response);
     *   }
     * });
     */
    reverseGeocode
};

function geocode(options: GeocodeOptions): Cancelable {

    //build url params
    const urlParams = _geocodeQueryString(options);

    //build request params
    const geocodeUrl = `${urls.REST_URL}/locations?${urlParams}`;
    const geocodeParameters = {
        'url': geocodeUrl,
        'headers': {
            'Authorization': getApiTokenOrKey(config)
        }
    };

    //make call
    const request = getJSON(geocodeParameters, (error, data) => {
        if (error) {
            if (options.failure !== undefined) {
                options.failure(error);
            }
        } else if (data) {
            if (options.success !== undefined) {
                options.success((data: any));
            }
        }
    });

    return {cancel: () => request.cancel()};
}

function reverseGeocode(options: ReverseGeocodeOptions): Cancelable {

    //build url params
    const urlParams = _reverseGeocodeQueryString(options);

    //build request params
    const reverseGeocodeUrl = `${urls.REST_URL}/locations?${urlParams}`;
    const reverseGeocodeParameters = {
        'url': reverseGeocodeUrl,
        'headers': {
            'Authorization': getApiTokenOrKey(config)
        }
    };

    //make call
    const request = getJSON(reverseGeocodeParameters, (error, data) => {
        if (error) {
            if (options.failure !== undefined) {
                options.failure(error);
            }
        } else if (data) {
            if (options.success !== undefined) {
                options.success((data: any));
            }
        }
    });

    return {cancel: () => request.cancel()};
}

function _geocodeQueryString(options: GeocodeOptions) {
    const queryString = [];

    if (options.address.region !== undefined) {
        //check region enum
        if (!Object.values(Common.Region).includes(options.address.region)) throw new Error(`${String(options.address.region)} is not a valid region. See TrimbleMaps.Common.Region for accepted values.`);
        queryString.push(`region=${String(options.address.region).toUpperCase()}`);
    }

    if (options.address.dataset !== undefined) {
        if (!Object.values(Common.DataVersion).includes(options.address.dataset)) throw new Error(`${String(options.address.dataset)} is not a valid data set. See TrimbleMaps.Common.DataVersion for accepted values.`);
        queryString.push(`dataset=${String(options.address.dataset)}`);
    } else {
        queryString.push(`dataset=Current`);
    }

    if (options.address.addr !== undefined) {
        queryString.push(`street=${options.address.addr}`);
    }

    if (options.address.city !== undefined) {
        queryString.push(`city=${options.address.city}`);
    }

    if (options.address.state !== undefined) {
        queryString.push(`state=${options.address.state}`);
    }

    if (options.address.zip !== undefined) {
        queryString.push(`postcode=${options.address.zip}`);
    }

    if (options.address.country !== undefined) {
        queryString.push(`country=${options.address.country}`);
    }

    if (options.address.postcodeFilter !== undefined) {
        queryString.push(`postcodeFilter=${options.address.postcodeFilter}`);
    }

    if (options.address.splc !== undefined) {
        queryString.push(`splc=${options.address.splc}`);
    }

    if (typeof (options.listSize) === "number") {
        queryString.push(`list=${String(options.listSize)}`);
    } else {
        queryString.push(`list=1`);
    }

    if (options.address.searchString !== undefined) {
        queryString.push(`searchString=${options.address.searchString}`);
    }

    return queryString.join('&');
}

function _reverseGeocodeQueryString(options: ReverseGeocodeOptions) {
    const queryString = [];

    if (options.region !== undefined) {
        //check region enum
        if (!Object.values(Common.Region).includes(options.region)) throw new Error(`${String(options.region)} is not a valid region. See TrimbleMaps.Common.Region for accepted values.`);
        queryString.push(`region=${String(options.region).toUpperCase()}`);
    }

    if (options.dataset !== undefined) {
        if (!Object.values(Common.DataVersion).includes(options.dataset)) throw new Error(`${String(options.dataset)} is not a valid data set. See TrimbleMaps.Common.DataVersion for accepted values.`);
        queryString.push(`dataset=${String(options.dataset)}`);
    } else {
        queryString.push(`dataset=Current`);
    }

    if (options.lonLat !== undefined) {
        queryString.push(`coords=${LngLat.convert(options.lonLat).toArray().join('%2C')}`);
    }

    return queryString.join('&');
}
